import { useRazorpay } from 'react-razorpay';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCallback, useRef, useState } from 'react';
import {
  EVENTS,
  PROPERTIES,
  trackEvent,
  TRACKING_SOURCES,
} from '@/services/posthog';
import { confirmGuestPayment, createGuestOrder } from '@/services/slaydAPI';
import useToastStore from '@/stores/ToastStore';
import { ConfirmPaymentStatus, type GuestOrder } from '@/types/Order.types';
import type { Product } from '@/types/Product.types';

declare const process: {
  env: {
    RAZORPAY_KEY_ID?: string;
  };
};

type PaymentStatus =
  | 'idle'
  | 'creating_order'
  | 'awaiting_payment'
  | 'confirming_payment'
  | 'success'
  | 'error';

interface RazorpayResponse {
  razorpay_payment_id: string;
  razorpay_signature: string;
  razorpay_order_id: string;
}

export interface UseRazorpayWebResult {
  initiatePayment: (
    product: Product,
    size: string | undefined,
    curatorName?: string,
    curationId?: string
  ) => Promise<GuestOrder | null>;
  status: PaymentStatus;
  error: string | null;
  isSDKLoading: boolean;
}

export const useRazorpayWeb = (): UseRazorpayWebResult => {
  const { Razorpay, isLoading: isSDKLoading } = useRazorpay();
  const [status, setStatus] = useState<PaymentStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const isInProgressRef = useRef(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const showToast = useToastStore((state) => state.showToast);

  const initiatePayment = useCallback(
    async (
      product: Product,
      size: string | undefined,
      curatorName?: string,
      curationId?: string
    ): Promise<GuestOrder | null> => {
      if (isInProgressRef.current) return null;

      if (isSDKLoading || !Razorpay) {
        trackEvent(EVENTS.RAZORPAY_SDK_NOT_READY, {
          [PROPERTIES.PRODUCT_ID]: product.id,
          [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
        });
        return null;
      }

      isInProgressRef.current = true;
      setStatus('creating_order');
      setError(null);

      trackEvent(EVENTS.CHECKOUT_STARTED, {
        [PROPERTIES.PRODUCT_ID]: product.id,
        [PROPERTIES.PRODUCT_NAME]: product.name,
        [PROPERTIES.BRAND]: product.brand || '',
        [PROPERTIES.PRICE]: product.discountPrice || product.price,
        [PROPERTIES.SELECTED_SIZE]: size || '',
        [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
      });

      let guestOrder: GuestOrder | undefined;
      let paymentStep: 'order_creation' | 'razorpay' | 'confirmation' =
        'order_creation';

      try {
        guestOrder = await createGuestOrder({
          productId: product.id,
          quantity: 1,
          ...(size ? { size } : {}),
        });

        paymentStep = 'razorpay';
        setStatus('awaiting_payment');

        trackEvent(EVENTS.ORDER_CREATED, {
          [PROPERTIES.GUEST_ORDER_ID]: guestOrder.guestOrderId,
          [PROPERTIES.PRODUCT_ID]: product.id,
          [PROPERTIES.PRODUCT_NAME]: product.name,
          [PROPERTIES.BRAND]: product.brand || '',
          [PROPERTIES.SELECTED_SIZE]: size || '',
          [PROPERTIES.AMOUNT]: guestOrder.razorpayAmount || '',
          [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
        });

        const currentGuestOrderId = guestOrder.guestOrderId;
        const currentAmount = guestOrder.razorpayAmount;

        const amountInPaise = Math.round(
          parseFloat(guestOrder.razorpayAmount || '0') * 100
        );

        const options = {
          key: (process.env.RAZORPAY_KEY_ID as string) || '',
          amount: amountInPaise,
          currency: 'INR' as const,
          name: 'Slayd Technologies Private Limited',
          order_id: guestOrder.razorpayOrderId || '',
          theme: { color: '#ffa8cd' },
          handler: async (response: RazorpayResponse) => {
            paymentStep = 'confirmation';
            setStatus('confirming_payment');
            trackEvent(EVENTS.RAZORPAY_SUCCESS, {
              [PROPERTIES.GUEST_ORDER_ID]: currentGuestOrderId,
              [PROPERTIES.PAYMENT_ID]: response.razorpay_payment_id,
              [PROPERTIES.PRODUCT_ID]: product.id,
              [PROPERTIES.PRODUCT_NAME]: product.name,
              [PROPERTIES.BRAND]: product.brand || '',
              [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
            });

            try {
              await confirmGuestPayment(currentGuestOrderId, {
                paymentStatus: ConfirmPaymentStatus.SUCCESS,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature,
              });
              setStatus('success');

              trackEvent(EVENTS.PAYMENT_CONFIRMED, {
                [PROPERTIES.GUEST_ORDER_ID]: currentGuestOrderId,
                [PROPERTIES.PRODUCT_ID]: product.id,
                [PROPERTIES.PRODUCT_NAME]: product.name,
                [PROPERTIES.BRAND]: product.brand || '',
                [PROPERTIES.AMOUNT]: currentAmount || '',
                [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
              });

              const key = searchParams.get('key');
              const rid = searchParams.get('rid');
              const variant = searchParams.get('variant');
              const fbclid = searchParams.get('fbclid');

              const orderPlacedParams = new URLSearchParams();
              orderPlacedParams.set(
                'guestOrderId',
                String(currentGuestOrderId)
              );
              if (curatorName)
                orderPlacedParams.set('curatorName', curatorName);
              if (curationId) orderPlacedParams.set('curation_id', curationId);
              if (key) orderPlacedParams.set('key', key);
              if (rid) orderPlacedParams.set('rid', rid);
              if (variant) orderPlacedParams.set('variant', variant);
              if (fbclid) orderPlacedParams.set('fbclid', fbclid);

              showToast('Payment successful!');
              navigate(`/order-placed?${orderPlacedParams.toString()}`);
            } catch (err) {
              console.error('Payment confirmation failed:', err);
              setStatus('error');
              setError(
                'Failed to confirm payment status. Please contact support.'
              );
              showToast('Verification failed. Staging order status.');

              const confirmFailureReason =
                err instanceof Error ? err.message : 'unknown';
              trackEvent(EVENTS.PAYMENT_CONFIRM_FAILED, {
                [PROPERTIES.GUEST_ORDER_ID]: currentGuestOrderId,
                [PROPERTIES.PRODUCT_ID]: product.id,
                [PROPERTIES.PRODUCT_NAME]: product.name,
                [PROPERTIES.BRAND]: product.brand || '',
                [PROPERTIES.FAILURE_REASON]: confirmFailureReason,
                [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
              });
            }
          },
          modal: {
            ondismiss: () => {
              isInProgressRef.current = false;
              setStatus('idle');
              showToast('Payment was cancelled');

              trackEvent(EVENTS.RAZORPAY_CANCELLED, {
                [PROPERTIES.GUEST_ORDER_ID]: currentGuestOrderId,
                [PROPERTIES.PRODUCT_ID]: product.id,
                [PROPERTIES.PRODUCT_NAME]: product.name,
                [PROPERTIES.BRAND]: product.brand || '',
                [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
              });

              confirmGuestPayment(currentGuestOrderId, {
                paymentStatus: ConfirmPaymentStatus.FAILED,
              }).catch((e) =>
                console.error('Error reporting cancel status', e)
              );
            },
          },
        };

        trackEvent(EVENTS.RAZORPAY_OPENED, {
          [PROPERTIES.GUEST_ORDER_ID]: guestOrder.guestOrderId,
          [PROPERTIES.PRODUCT_ID]: product.id,
          [PROPERTIES.PRODUCT_NAME]: product.name,
          [PROPERTIES.BRAND]: product.brand || '',
          [PROPERTIES.AMOUNT]: guestOrder.razorpayAmount || '',
          [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
        });

        const rzp = new Razorpay(options);
        rzp.open();
        return guestOrder;
      } catch (err: unknown) {
        console.error('Checkout error:', err);

        let errMsg = 'Payment failed';
        if (err instanceof Error) {
          errMsg = err.message;
        } else if (err && typeof err === 'object' && 'response' in err) {
          const response = (
            err as { response?: { data?: { message?: string } } }
          ).response;
          if (response?.data?.message) {
            errMsg = response.data.message;
          }
        }

        setError(errMsg);
        setStatus('error');
        showToast(errMsg);

        const failureReason = err instanceof Error ? err.message : 'unknown';
        if (paymentStep === 'order_creation') {
          trackEvent(EVENTS.ORDER_CREATE_FAILED, {
            [PROPERTIES.PRODUCT_ID]: product.id,
            [PROPERTIES.PRODUCT_NAME]: product.name,
            [PROPERTIES.BRAND]: product.brand || '',
            [PROPERTIES.FAILURE_REASON]: failureReason,
            [PROPERTIES.SELECTED_SIZE]: size || '',
            [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
          });
        } else if (paymentStep === 'razorpay') {
          trackEvent(EVENTS.RAZORPAY_FAILURE, {
            [PROPERTIES.GUEST_ORDER_ID]: guestOrder?.guestOrderId || 0,
            [PROPERTIES.PRODUCT_ID]: product.id,
            [PROPERTIES.PRODUCT_NAME]: product.name,
            [PROPERTIES.BRAND]: product.brand || '',
            [PROPERTIES.FAILURE_REASON]: failureReason,
            [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
          });
        }

        if (guestOrder) {
          confirmGuestPayment(guestOrder.guestOrderId, {
            paymentStatus: ConfirmPaymentStatus.FAILED,
          }).catch((e) => console.error('Error reporting failed status', e));
        }

        throw err;
      } finally {
        if (status !== 'awaiting_payment') {
          isInProgressRef.current = false;
        }
      }
    },
    [Razorpay, isSDKLoading, navigate, searchParams, showToast, status]
  );

  return { initiatePayment, status, error, isSDKLoading };
};
export default useRazorpayWeb;
